import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../dataManagement/uiDb/UiDb";
import { fetchMyBusinesses } from "../services/thunks/BusinessThunk";
import { BusinessProfile } from "../interfaces/BusinessObject";
import genericUtility from "../services/utility/GenericUtility";

interface UseMyBusinessesResult {
  businesses: BusinessProfile[];
  isLoading: boolean;
  error: string | null;
}

export function useMyBusinesses(): UseMyBusinessesResult {
  const dispatch: AppDispatch = useDispatch();
  const didFetchRef = useRef(false);

  const businesses = useSelector((state: RootState) => state.business.myBusinesses);
  const loadingMap = useSelector((state: RootState) => state.business.loadingMap);
  const errorMap = useSelector((state: RootState) => state.business.errorMap);
  const successMap = useSelector((state: RootState) => state.business.successMap);

  useEffect(() => {
    // Skip the authenticated fetch for anonymous users (e.g. public product-details route).
    // didFetchRef stays false so the fetch still fires once the user logs in and remounts.
    if (!genericUtility.isUserLoggedIn()) return;
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    // Multiple components on the same page (e.g. CtaSection + QueriesSection on
    // product details) each use this hook — guard against firing a duplicate
    // request when another instance already has one in flight or completed.
    if (loadingMap.fetchMyBusinesses || successMap.fetchMyBusinesses) return;
    dispatch(fetchMyBusinesses());
  }, [dispatch, loadingMap.fetchMyBusinesses, successMap.fetchMyBusinesses]);

  return {
    businesses,
    isLoading: !!loadingMap.fetchMyBusinesses,
    error: errorMap.fetchMyBusinesses ?? null,
  };
}
